// Path-addressable reactive store
// Factory function per architecture rules

import type { AppContext } from "../types/index.js";
import { z } from "zod";

export type StoreChangeSource =
  | "local"
  | "uiStateAdapter"
  | "socket"
  | "hydration";

export interface StoreSetMeta {
  readonly source?: StoreChangeSource;
}

export type PathSelector = string | RegExp | ((path: string) => boolean);

export interface SubscriptionOptions<T = unknown> {
  readonly debounceMs?: number;
  readonly equals?: (a: T, b: T) => boolean;
}

export interface PathStoreAPI {
  readonly get: <T = unknown>(path: string) => T | undefined;
  readonly set: <T = unknown>(
    path: string,
    value: T,
    meta?: StoreSetMeta
  ) => void;
  readonly subscribe: <T = unknown>(
    selector: PathSelector,
    listener: (payload: {
      path: string;
      value: T;
      prev: T | undefined;
      source: StoreChangeSource;
    }) => void,
    options?: SubscriptionOptions<T>
  ) => () => void;
  readonly batch: (fn: () => void) => void;
  readonly withHydrationWindow: (ms: number, fn: () => void) => void;
  readonly registerSchema: (prefix: string, schema: z.ZodTypeAny) => void;
}

export const createPathStore = (appContext: AppContext): PathStoreAPI => {
  const state = new Map<string, unknown>();
  const listeners = new Set<{
    selector: PathSelector;
    listener: (payload: {
      path: string;
      value: unknown;
      prev: unknown;
      source: StoreChangeSource;
    }) => void;
    debounceMs: number;
    equals?: (a: unknown, b: unknown) => boolean;
    timer?: number;
  }>();

  const schemas = new Map<string, z.ZodTypeAny>();

  let batching = false;
  const pending: Array<{
    path: string;
    value: unknown;
    prev: unknown;
    source: StoreChangeSource;
  }> = [];
  let hydrating = false;
  let hydrationTimer: number | null = null;

  const selectorMatches = (selector: PathSelector, path: string): boolean => {
    if (typeof selector === "string")
      return selector === path || path.startsWith(selector);
    if (selector instanceof RegExp) return selector.test(path);
    return selector(path);
  };

  const shallowEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (typeof a !== "object" || typeof b !== "object") return false;
    const ak = Object.keys(a as any);
    const bk = Object.keys(b as any);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if ((a as any)[k] !== (b as any)[k]) return false;
    }
    return true;
  };

  const validate = (path: string, value: unknown): unknown => {
    // Find most specific registered schema by prefix match
    let best: { prefix: string; schema: z.ZodTypeAny } | null = null;
    for (const [prefix, schema] of schemas) {
      if (path.startsWith(prefix)) {
        if (!best || prefix.length > best.prefix.length)
          best = { prefix, schema };
      }
    }
    if (!best) return value;
    const res = best.schema.safeParse(value);
    if (!res.success) {
      console.warn(
        "PathStore: schema validation failed for",
        path,
        res.error.issues
      );
      throw new Error("PathStore schema validation failed");
    }
    return res.data;
  };

  const emit = (change: {
    path: string;
    value: unknown;
    prev: unknown;
    source: StoreChangeSource;
  }) => {
    if (batching) {
      pending.push(change);
      return;
    }

    // Notify store subscribers
    for (const sub of listeners) {
      if (!selectorMatches(sub.selector, change.path)) continue;

      const invoke = () => sub.listener(change);
      if (sub.debounceMs > 0) {
        if (sub.timer) clearTimeout(sub.timer);
        // @ts-ignore - browser env
        sub.timer = setTimeout(invoke, sub.debounceMs) as unknown as number;
      } else {
        invoke();
      }
    }

    // Bridge to event bus
    try {
      appContext.eventBus.emit("state:changed", change);
    } catch {}
  };

  const api: PathStoreAPI = {
    get: (path) => state.get(path) as any,
    set: (path, value, meta) => {
      const source: StoreChangeSource =
        meta?.source || (hydrating ? "hydration" : "local");

      const validated = validate(path, value);
      const prev = state.get(path);

      // Equality check
      const eq = shallowEqual(prev, validated);
      if (eq) return;

      state.set(path, validated);
      emit({ path, value: validated, prev, source });
    },
    subscribe: (selector, listener, options) => {
      const sub = {
        selector,
        listener: listener as any,
        debounceMs: options?.debounceMs ?? 0,
        equals: options?.equals as any,
      };
      listeners.add(sub);
      return () => listeners.delete(sub);
    },
    batch: (fn) => {
      const prev = batching;
      batching = true;
      try {
        fn();
      } finally {
        batching = prev;
        if (!batching && pending.length) {
          const toEmit = pending.splice(0, pending.length);
          for (const change of toEmit) emit(change);
        }
      }
    },
    withHydrationWindow: (ms, fn) => {
      hydrating = true;
      if (hydrationTimer) clearTimeout(hydrationTimer);
      try {
        fn();
      } finally {
        // @ts-ignore - browser env
        hydrationTimer = setTimeout(() => {
          hydrating = false;
          hydrationTimer = null;
        }, Math.max(0, ms)) as unknown as number;
      }
    },
    registerSchema: (prefix, schema) => {
      schemas.set(prefix, schema);
    },
  };

  return api;
};

export type { z };
